import React from 'react';
import { Flex, Caption, useStyles } from '@archipro-design/aria';
import Download from './Download';
import * as S from './Downloads.style';
import type LibraryDownload from '@modules/product/type/library-download';
import type FileCategory from '@modules/product/type/file-category';

interface DownloadsProps {
    data: {
        LibraryDownloads: LibraryDownload[];
    };
}

interface CategoryGroup {
    category: FileCategory;
    downloads: LibraryDownload[];
}

interface Category {
    ID: number;
    Title: string;
    SortOrder: number;
}

const defaultCategory = {
    ID: 0,
    Title: 'Other',
    SortOrder: -1,
} as Category;

const Downloads = ({ data: { LibraryDownloads } }: DownloadsProps) => {
    const { css } = useStyles();
    const validDownloads = LibraryDownloads.filter(
        (d) => d.InternalLink || d.ExternalLink
    );
    if (validDownloads.length === 0) {
        return null;
    }
    const grouped = groupDownloadsByCategory(validDownloads);
    return (
        <Flex column={true} className={css(S.Groups)}>
            {grouped.map((g) => (
                <Flex
                    key={`download-category-${g.category.ID}`}
                    vAlign={'start'}
                >
                    <div className={css(S.GroupLabel)}>
                        {g.category ? (
                            <Caption variant="03">
                                {g.category?.Title ?? ''}
                            </Caption>
                        ) : null}
                    </div>
                    <Flex
                        hAlign={'start'}
                        vAlign={'start'}
                        wrap={true}
                        className={css(S.Group)}
                    >
                        {g.downloads.map((d) => (
                            <div
                                key={`file-download-${d.ID}`}
                                className={css(S.DownloadWrapper)}
                            >
                                <Download data={d} />
                            </div>
                        ))}
                    </Flex>
                </Flex>
            ))}
        </Flex>
    );
};

const groupDownloadsByCategory = (
    downloads: LibraryDownload[]
): CategoryGroup[] => {
    const categories: Record<number, Category> = [];
    downloads
        .map((i) => (i.FileCategory ?? defaultCategory) as Category)
        .sort((a, b) => b.SortOrder - a.SortOrder)
        .forEach((i) => {
            categories[i.ID] = i;
        });

    return Object.values(categories)
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((category) => {
            const items = downloads
                .filter((d) => (d.FileCategory?.ID ?? 0) === category.ID)
                .sort((a, b) => b.MemberSortOrder - a.MemberSortOrder);
            const newCategory =
                category.Title === 'BPIR'
                    ? {
                          ...category,
                          Title: 'Building Product Information Requirements',
                      }
                    : category;
            return {
                category: newCategory,
                downloads: items,
            } as CategoryGroup;
        });
};

export default Downloads;
